import React, { useEffect, useState } from "react";
import { Box, Modal } from "@mui/material";
import { Button as Btn, Typography } from "@material-ui/core";
import { DarkGrey, PrimaryColor } from "../../shared/styles/colors";
import { Spinner } from "../../components";
import Project from "../../model/project";
import UploadIcon from "../../assets/admin/images-icon.svg";
import CloseIcon from "@mui/icons-material/Close";
import ImageList from "@mui/material/ImageList";
import ImageListItem from "@mui/material/ImageListItem";
import ImageListItemBar from "@mui/material/ImageListItemBar";
import FullscreenImage from "../../shared/components/images/FullscreenImage";
import useStyles from "./styles";
import ApiService from "../../service/ApiService";
import ProjectImage from "../../model/project-image";
import { DatabaseEntities } from "../../model/enum/database-entities.enum";
import { ToastContainer } from "react-toastify";
import AddImageModal from "./AddImageModal";
import FirebaseService from "../../service/FirebaseService";
import AlertService from "../../service/AlertService";
import ToastMessages from "../../model/enum/toast-messages.enum";
import Misc from "../../utils/Misc";

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "70rem",
  bgcolor: "background.paper",
  border: "none",
  borderRadius: "8px",
  boxShadow: 24,
  pt: 2,
  px: 4,
  pb: 3,
  maxHeight: "90vh", // Restrict maximum height to 80% of the viewport height
  overflowY: "auto", // Enable vertical scrolling
  "@media (max-width: 640px)": {
    width: "88%",
  },
};

interface Props {
  project: Project;
}

const ProjectImagesModal = ({ project }: Props) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [items, setItems] = useState<ProjectImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [imageUrl, setImageUrl] = useState<string>();
  const [fullScreen, setFullScreen] = useState(false);
  const [itemsToDelete, setItemsToDelete] = useState<number[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await ApiService.get<ProjectImage[]>(
        `${DatabaseEntities.PROJECTS}/${project.id}/${DatabaseEntities.IMAGES}`,
      ).then((data: ProjectImage[]) => {
        if (Misc.isArrayEmpty(data)) return;
        setItems(data);
      });
      setLoading(false);
    };

    if (open && Misc.isArrayEmpty(items)) {
      fetchData();
    }
  }, [open, items]);

  const toggleFullScreen = (url: string) => {
    setImageUrl(url);
    setFullScreen((fullScreen) => !fullScreen);
  };

  const handleToggleImage = async (e: any, id: number) => {
    if (e.target.checked) {
      setItemsToDelete((prevItems) => [...prevItems, id]);
    } else {
      setItemsToDelete((prevItems) =>
        prevItems.filter((itemId) => itemId !== id),
      );
    }
  };

  const handleDelete = async () => {
    if (!emptyDeleteList()) {
      setLoading(true);
      const data = {
        projectId: project.id,
        imageIds: itemsToDelete,
      };

      await ApiService.delete(
        `${DatabaseEntities.PROJECTS}/${DatabaseEntities.IMAGES}`,
        data,
      )
        .then(() => {
          setItems((prevItems) =>
            prevItems.filter((item) => !itemsToDelete.includes(item.id)),
          );
        })
        .then(async () => {
          // Delete images from Firebase Storage
          itemsToDelete
            .map((id) => {
              const item = items.find((item) => item.id === id);
              return item ? item.imageUrl : "";
            })
            .forEach(async (imageUrl) => {
              if (!imageUrl) return;
              await FirebaseService.deleteFile(imageUrl).catch((error) => {
                AlertService.error(ToastMessages.IMAGES_ERROR_DELETING);
                console.error(`${ToastMessages.IMAGES_ERROR_FIREBASE}:`, error);
              });
            });
          AlertService.success(ToastMessages.IMAGES_DELETED);
        })
        .catch(() => {
          AlertService.error(ToastMessages.IMAGES_ERROR_SERVER);
        });
      setLoading(false);
    }
  };

  const emptyDeleteList = () => {
    return Misc.isArrayEmpty(itemsToDelete);
  };

  return (
    <div>
      <Btn style={{ textTransform: "none" }} onClick={handleOpen}>
        <img
          src={UploadIcon}
          alt="upload icon"
          style={{ color: DarkGrey, width: 20, marginRight: "5px" }}
        />
        Manage Images
      </Btn>
      {open && (
        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          sx={{ overflowY: "auto" }}
        >
          <Box sx={style}>
            <div className={classes.headerDiv}>
              <div className={classes.headerSubDiv2}>
                <Typography className={classes.title}>Images</Typography>
              </div>
              <Btn onClick={handleClose}>
                <CloseIcon />
              </Btn>
            </div>
            <hr />
            {loading && <Spinner />}
            <div className="d-flex row justify-content-center">
              {fullScreen && (
                <FullscreenImage
                  imageUrl={imageUrl}
                  toggleFullScreen={toggleFullScreen}
                />
              )}
              <ImageList sx={{ width: 1000, maxHeight: 600 }} cols={3} gap={8}>
                {items.map((item) => (
                  <ImageListItem
                    key={item.id}
                    sx={{ cursor: "zoom-in", maxHeight: 350, width: 300 }}
                  >
                    <img
                      srcSet={`${item}?w=164&h=164&fit=crop&auto=format&dpr=2 2x`}
                      src={`${item.imageUrl}`}
                      alt={`${item.id}`}
                      loading="lazy"
                      onClick={() => toggleFullScreen(item.imageUrl)}
                    />
                    <ImageListItemBar
                      actionIcon={
                        <input
                          type="checkbox"
                          style={{
                            accentColor: PrimaryColor,
                            cursor: "pointer",
                          }}
                          onChange={(e) => handleToggleImage(e, item.id)}
                        />
                      }
                      actionPosition="right"
                    />
                  </ImageListItem>
                ))}
              </ImageList>
            </div>
            <div className={classes.buttonContainer}>
              <div className={classes.buttonDiv}>
                <AddImageModal
                  project={project}
                  items={items}
                  setItems={setItems}
                />
              </div>
              <div className={classes.buttonDiv}>
                <Btn
                  className={classes.button}
                  onClick={handleDelete}
                  disabled={emptyDeleteList()}
                >
                  Delete
                </Btn>
              </div>
            </div>
          </Box>
        </Modal>
      )}
      <ToastContainer />
    </div>
  );
};

export default ProjectImagesModal;
